/* eslint-disable react-hooks/exhaustive-deps */
import { Box, CircularProgress, Typography } from "@material-ui/core";
import _ from "lodash";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/common";
import useQuery from "../../hooks/useQuery";
import useSessionUser from "../../hooks/useSessionUser";
import { rootReducersState } from "../../reducers";
import {
  jobListRequest,
  jobListReset,
  publicJobListRequest,
} from "../../reducers/job/jobs.reducer";
import appRoutes from "../../routes/app.routes";
import { jobTypes, timeDurations } from "../../utils/appConstants";
import JobSidebar, { Filters } from "../../components/Jobs/JobSidebar";
import JobListings from "../../components/Jobs/JobListings";
import SimilarJobs from "../../components/Jobs/SimilarJobs";

const defaultFilter = {
  page: 1,
  pageSize: 12,
  jobType: jobTypes.ALL.key,
  jobDuration: timeDurations.ALL.key,
};

const JobSearch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryPrams = useQuery();
  const tmpSearchTerm = queryPrams.get("q") ?? "";
  const { jobs } = useSelector(({ job }: rootReducersState) => job);
  const token = useSelector((state: rootReducersState) => state.session.token);
  const tokenData = !!token;
  const role = useSelector((state: rootReducersState) =>
    String(_.get(state.session.currentUser, "role", ""))
  );
  const pagination = _.get(jobs, "pagination", {});
  const jobPostings = _.get(jobs, "data", []);
  const loading = _.get(jobs, "loading", true);
  const { IS_COMPANY } = useSessionUser();

  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState(() => {
    const q = (queryPrams.get("q") || "").trim();
    return {
      ...defaultFilter,
      ...(q ? { q } : {}),
    };
  });
  const [jobTitleSearch, setJobTitleSearch] = useState(
    () => (queryPrams.get("q") || "").trim()
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [isSavedChanged, setIsSavedChanged] = useState<boolean>(false);
  const [similarReady, setSimilarReady] = useState(false);
  const [listResetPending, setListResetPending] = useState(false);
  const listWasLoading = useRef(false);
  const handleSimilarSettled = useCallback(() => setSimilarReady(true), []);

  const mapSidebarFiltersToAPI = useCallback(
    (sidebarFilters: Partial<Filters>) => {
      const apiFilter: any = { page: 1, pageSize: 12 };

      if (sidebarFilters.relevant != null) {
        if (sidebarFilters.relevant === "Week")
          apiFilter.jobDuration = timeDurations.WEEK.key;
        else if (sidebarFilters.relevant === "Month")
          apiFilter.jobDuration = timeDurations.MONTH.key;
        else apiFilter.jobDuration = timeDurations.ALL.key;
      }

      if (
        sidebarFilters.locations &&
        !sidebarFilters.locations.includes("All")
      ) {
        if (sidebarFilters.locations.includes("On site"))
          apiFilter.isRemote = "1";
        else if (sidebarFilters.locations.includes("Remote"))
          apiFilter.isRemote = "2";
        else if (sidebarFilters.locations.includes("Hybrid"))
          apiFilter.isRemote = "3";
      } else {
        apiFilter.isRemote = "0";
      }

      if (sidebarFilters.jobType && !sidebarFilters.jobType.includes("All")) {
        const jobTypeMap: any = {
          "Full Time": jobTypes.FULL_TIME.key,
          "Part Time": jobTypes.PART_TIME.key,
          Contract: jobTypes.CONTRACT.key,
          Internship: jobTypes.INTERNSHIP.key,
        };
        apiFilter.jobType =
          jobTypeMap[sidebarFilters.jobType[0]] || jobTypes.ALL.key;
      } else {
        apiFilter.jobType = jobTypes.ALL.key;
      }

      if (
        sidebarFilters.experience &&
        !sidebarFilters.experience.includes("All")
      ) {
        apiFilter.experienceLevel = sidebarFilters.experience.join(",");
      } else {
        apiFilter.experienceLevel = undefined;
      }

      const currencyId =
        sidebarFilters.currency != null && sidebarFilters.currency > 0
          ? sidebarFilters.currency
          : 0;

      if (sidebarFilters.salaryRange) {
        const [min, max] = sidebarFilters.salaryRange;
        const isHourly = sidebarFilters.salaryTerm === "per_hour";
        const defaultMax = isHourly ? 500 : 3000;
        if (min > 0 || max < defaultMax) {
          apiFilter.minSalary = isHourly ? min : min * 1000;
          apiFilter.maxSalary = isHourly ? max : max * 1000;
        } else {
          apiFilter.minSalary = undefined;
          apiFilter.maxSalary = undefined;
        }
      }

      apiFilter.currency = currencyId > 0 ? currencyId : undefined;
      apiFilter.salaryTerm = sidebarFilters.salaryTerm;

      const trimmedLocation = sidebarFilters.searchLocation?.trim() ?? "";
      apiFilter.location = trimmedLocation || undefined;

      return apiFilter;
    },
    []
  );

  const handleSidebarFilterChange = useCallback(
    (changedFilters: Partial<Filters>) => {
      const apiFilters = mapSidebarFiltersToAPI(changedFilters);
      setPage(1);
      setListResetPending(true);
      setFilter((prev) => {
        const merged: Record<string, unknown> = {
          ...prev,
          ...apiFilters,
          page: 1,
        };
        for (const key of Object.keys(apiFilters)) {
          if (apiFilters[key as keyof typeof apiFilters] === undefined) {
            delete merged[key];
          }
        }
        return merged as typeof prev;
      });
      dispatch(jobListReset({ ...apiFilters, page: 1 }));
    },
    [dispatch, mapSidebarFiltersToAPI]
  );

  useEffect(() => {
    if (role === "candidate" && tokenData) dispatch(jobListRequest(filter));
    else dispatch(publicJobListRequest(filter));
    setIsFetching(false);
  }, [filter, dispatch, role, tokenData, isSavedChanged]);

  /** Keep search input in sync when `?q=` changes (back/forward, shared URL). */
  useEffect(() => {
    setJobTitleSearch((tmpSearchTerm || "").trim());
  }, [tmpSearchTerm]);

  /**
   * Debounced title search → `filter.q` → careers API (`Helper.jobFilters` uses `q` for title/description/company).
   * Skip updates when the value matches `filter.q` to avoid duplicate fetches on mount.
   */
  useEffect(() => {
    const id = window.setTimeout(() => {
      const trimmed = jobTitleSearch.trim();
      setFilter((prev) => {
        const prevQ = (prev.q || "").trim();
        if (trimmed === prevQ) return prev;
        queueMicrotask(() => setPage(1));
        const next = { ...prev, page: 1 };
        if (trimmed) next.q = trimmed;
        else delete next.q;
        return next;
      });
    }, 500);
    return () => window.clearTimeout(id);
  }, [jobTitleSearch]);

  const currentListPage =
    pagination?.current ?? pagination?.page ?? page;

  const loadMoreJobs = useCallback(() => {
    const totalPages = pagination?.total ?? 0;
    if (!isFetching && !loading && currentListPage < totalPages) {
      const nextPage = currentListPage + 1;
      setPage(nextPage);
      setFilter((prev) => ({ ...prev, page: nextPage }));
      setIsFetching(true);
    }
  }, [currentListPage, loading, pagination, isFetching]);

  const handleJobHubRoute = () =>
    navigate(appRoutes.candidateJobHub.generatePath("saved"));

  useEffect(() => {
    setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    if (loading) {
      listWasLoading.current = true;
      return;
    }
    if (listWasLoading.current) {
      listWasLoading.current = false;
      setListResetPending(false);
    }
  }, [loading]);

  const syncSimilar = tokenData && !IS_COMPANY;
  const mainListPending =
    initialLoading ||
    listResetPending ||
    (loading && jobPostings.length === 0);
  const revealColumns = !mainListPending && (!syncSimilar || similarReady);

  return (
    <Box className="max-container">
      <Box className="job-search-wrapper">
        <Box className="filter-wrapper">
          <Box className="filter-row">
            <Typography className="total-result" variant="body1">
              About {pagination?.totalResult || 0} results
            </Typography>
            {tokenData && !IS_COMPANY && (
              <Button
                onClick={handleJobHubRoute}
                variant="contained"
                color="primary"
              >
                My Job Hub
              </Button>
            )}
          </Box>
        </Box>

        <Box
          className={`job-search-content${!tokenData || IS_COMPANY ? " no-similar-jobs" : ""
            }`}
        >
          <JobSidebar
            filterOpen={filterOpen}
            setFilterOpen={setFilterOpen}
            onFilterChange={handleSidebarFilterChange}
          />

          <Box className="job-search-main">
            <JobListings
              jobPostings={jobPostings}
              token={token}
              loading={loading}
              cardsPending={!revealColumns}
              pagination={pagination}
              onLoadMore={loadMoreJobs}
              setIsSavedChanged={setIsSavedChanged}
              searchTerm={jobTitleSearch}
              onSearchTermChange={setJobTitleSearch}
            />
            {revealColumns && (isFetching || loading) && jobPostings.length > 0 && (
              <Box className="see-more circle-center">
                <CircularProgress color="secondary" size={25} />
              </Box>
            )}
          </Box>

          {syncSimilar && (
            <SimilarJobs
              isJobHub={true}
              token={token}
              hold={!revealColumns}
              onSettled={handleSimilarSettled}
            />
          )}


        </Box>
      </Box>
    </Box>
  );
};

export default JobSearch;
